import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BrandLogo from '../../components/brand/BrandLogo';

/**
 * Shared shell for the three public legal pages (Privacy Policy, Terms of
 * Service, Contact Us). Handles SEO title + meta description, sticky brand
 * header, "Back to FABTEXS" link, and a mobile-friendly content wrapper.
 *
 * Kept dependency-free (no react-helmet) — sets the document title via
 * a useEffect so we don't have to ship a new library.
 */
export function LegalLayout({ title, description, lastUpdated, children }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', 'description');
        document.head.appendChild(m);
      }
      m.setAttribute('content', description);
    }
  }, [title, description]);

  return (
    <div className="min-h-screen bg-white text-fab-text">
      {/* Sticky brand header — gives every legal URL a clear FABTEXS context
         (required for Meta App Review reviewers). */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-fab-primary-lighter">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/home" className="flex items-center" data-testid="legal-brand">
            <BrandLogo height={28} />
          </Link>
          <Link to="/home" className="text-[12px] sm:text-[13px] font-semibold text-fab-text-secondary hover:text-fab-primary inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to FABTEXS
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-fab-text" data-testid="legal-title">{title}</h1>
        {lastUpdated && (
          <div className="text-[12px] text-fab-text-muted mt-2" data-testid="legal-last-updated">
            Last updated: <span className="font-mono">{lastUpdated}</span>
          </div>
        )}
        <div
          className="mt-8 space-y-6 text-[14.5px] sm:text-[15px] leading-relaxed text-fab-text-secondary
                     [&_h2]:font-display [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-bold [&_h2]:text-fab-text [&_h2]:mt-8
                     [&_h3]:font-semibold [&_h3]:text-[15px] [&_h3]:text-fab-text [&_h3]:mt-4
                     [&_p]:mt-3
                     [&_ul]:mt-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ul]:space-y-1
                     [&_ol]:mt-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_ol]:space-y-1
                     [&_a]:text-fab-primary [&_a]:font-semibold [&_a]:underline
                     [&_strong]:text-fab-text"
        >
          {children}
        </div>

        <footer className="mt-16 pt-6 border-t border-fab-primary-lighter text-[12.5px] text-fab-text-muted flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>© {new Date().getFullYear()} FABTEXS – India’s Wholesale Fabric Hub</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy-policy" className="hover:text-fab-primary font-semibold">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-fab-primary font-semibold">Terms of Service</Link>
            <Link to="/data-deletion" className="hover:text-fab-primary font-semibold">Data Deletion</Link>
            <Link to="/contact-us" className="hover:text-fab-primary font-semibold">Contact Us</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default LegalLayout;
